import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { City } from '@entities/Location'

import { useGeolocationCity } from '@features/location-picker/hooks/useGeolocationCity'

import { cn } from '@shared/lib/cn'
import Button from '@shared/ui/button'

import styles from './styles.module.scss'

interface CitySuggestionProps {
  className?: string
  currentCity: City | null
  onCitySelect?: (city: City) => void
  onDismiss?: () => void
  isLocationPickerOpen?: boolean
  isConfirmation?: boolean
}

export default function CitySuggestion ({
  className,
  currentCity,
  onCitySelect,
  onDismiss,
  isLocationPickerOpen = false,
  isConfirmation = false
}: CitySuggestionProps) {
  const { t } = useTranslation('common')
  const { suggestedCity, detectedCityName, loading: geolocationLoading, error } = useGeolocationCity()
  const [ hasDismissedSuggestion, setHasDismissedSuggestion ] = useState(false)
  // Show city suggestion popup if:
  // 1. No current city is selected (city is persisted, so if it exists, user already picked one) or to confirm selected city
  // 2. We have a detected city name (even if not in list) OR a suggested city OR geolocation was denied/errored
  // 3. It hasn't been dismissed
  // 4. Not loading
  // 5. Location picker is not open
  const showCitySuggestion = (!currentCity || (isConfirmation && currentCity)) &&
    (detectedCityName || suggestedCity || error) &&
    !hasDismissedSuggestion &&
    !geolocationLoading &&
    !isLocationPickerOpen

  // Debug logging
  useEffect(() => {
    console.log('CitySuggestion Debug:', {
      currentCity: currentCity?.name,
      detectedCityName,
      suggestedCity: suggestedCity?.name,
      hasDismissedSuggestion,
      geolocationLoading,
      isLocationPickerOpen,
      showCitySuggestion,
      error,
      willRender: showCitySuggestion && (suggestedCity || detectedCityName),
    })
  }, [ currentCity, detectedCityName, suggestedCity, hasDismissedSuggestion, geolocationLoading, isLocationPickerOpen, showCitySuggestion, error ])

  const handleConfirm = () => {
    if (suggestedCity) {
      onCitySelect?.(suggestedCity)
    }
    if (currentCity) {
      onCitySelect?.(currentCity)
    }

    setHasDismissedSuggestion(true)
  }

  const handleDismiss = () => {
    setHasDismissedSuggestion(true)
    onDismiss?.()
  }

  // Only show if we have a detected city name, suggested city, or error
  // If geolocation fails or no city is detected, show "choose city" message
  if (!showCitySuggestion) {
    return null
  }

  // If we have a suggested city (matched), show confirmation dialog
  // If we don't have a suggested city (not in list or geolocation denied), show "choose city" message
  const hasMatchedCity = !!suggestedCity || (isConfirmation && currentCity)
  const cityToConfirm = suggestedCity || currentCity

  return (
    <div className={cn(styles.citySuggestion, className)}>
      <div className={styles.citySuggestion__popup}>
        {hasMatchedCity ? (
          <>
            <p className={styles.citySuggestion__question}>
              {t('location.yourCityIs', { city: cityToConfirm?.name })}
            </p>
            <div className={styles.citySuggestion__actions}>
              <Button
                className={styles.citySuggestion__confirmButton}
                onClick={handleConfirm}
                theme='primary'
                size='medium'
              >
                {t('yes')}
              </Button>
              <Button
                className={styles.citySuggestion__chooseButton}
                onClick={handleDismiss}
                type="button"
                theme="grey"
              >
                {t('location.chooseAnotherCity')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className={styles.citySuggestion__question}>
              {t('location.chooseCityToContinue')}
            </p>
            <div className={styles.citySuggestion__actions}>
              <Button
                className={styles.citySuggestion__chooseButton}
                onClick={handleDismiss}
                type="button"
              >
                {t('location.chooseCity')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
